import { useNavigate } from "react-router";
import { NavigationBar } from "../../../../components/navigation/NavigationBar";
import { DISCOVER, DISCOVER_SEARCH } from "../../../../constants/routes";
import {
  Button,
  H5,
  H6,
  Icon,
  ProfilePicture,
  Spinner,
} from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import {
  ComponentProps,
  ReactNode,
  useEffect,
  useEffectEvent,
  useState,
} from "react";
import { DiscoveryHorizontalList } from "../components/DiscoveryHorizontalList";
import { useDiscoveryDistributionSupplierList } from "../../../../hooks/api/v2/discovery/useDiscoveryDistributionSupplierList";
import { DiscoveryV2DistributionSupplierCard } from "./DiscoveryV2DistributionSupplierCard";
import { twMerge } from "tailwind-merge";
import { pluralize } from "@sellar-io/common";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { unAuthorisedUserPostcodeInLocalStorageKey } from "../DiscoveryV2Home";
import { DiscoveryV2DistributionInformationDialog } from "./DiscoveryV2DistributionInformationDialog";
import { useMenuDrawerContext } from "../../../../components/menu/MenuDrawerContextProvider.shared";
import { useSellarUser } from "../../../../hooks";
import { useSearchParamsPostcodeCheck } from "../../../../hooks/useSearchParamsPostcodeCheck";
import { StorefrontPostcodeDialog } from "../../../stockList/categoryPage/Storefront/StorefrontDialogs/StorefrontPostcodeDialog/StorefrontPostcodeDialog";
import { useSellarDistributionAvailabilityList } from "../../../../hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityList";
import dayjs from "dayjs";
import {
  DiscoveryDistributionSupplierListResponseType,
  SellarDistributionAvailabilityListResponseType,
} from "@sellar-io/api-v2/types";
import isoWeek from "dayjs/plugin/isoWeek";
import { useDiscoveryV2DistributionTracking } from "./useDiscoveryV2DistributionTracking";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { DistributionEducationDialog } from "./DistributionEducationDialog";
import { DistributionEducationDialogPage } from "./DistributionEducationDialog.shared";
import { DraftBasketDrawer } from "./DraftBasketDrawer";
import { MarketBasketFeedbackProvider } from "./MarketBasketFeedbackContext";
import { MarketBasketFlyover } from "./MarketBasketFlyover";
import { useDraftDbsBaskets } from "./useDraftDbsBaskets";
import {
  ConsolidatedBasketOpenSource,
  useConsolidatedBasketTracking,
} from "./consolidatedOrderingTracking";
import { QuantityIndicator } from "../../../../components/quantityIndicator/QuantityIndicator";
import {
  DeliveredBySellarFilterOption,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";
import { useShallow } from "zustand/shallow";
import { IntersectingInfiniteLoadingButton } from "~/components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { DiscoveryListSupplierCard } from "../DiscoveryListSupplierCard";
import {
  UpcomingDeliveryGroup,
  useGetUpcomingDeliveryGroups,
} from "../lib/useGetUpcomingDeliveryGroups";
import { TrackedCutoffCountdown } from "./CutoffCountdown/TrackedCutoffCountdown";
import { useCanShowDeliveredBySellarOptionV2 } from "../lib/useCanShowDeliveredBySellarOptionV2";
import { Tooltip } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";

dayjs.extend(isoWeek);

const now = new Date();
const fridayOfCurrentWeek = dayjs(now).isoWeekday(5).endOf("day").toDate();
const startOfNextWeek = dayjs(fridayOfCurrentWeek)
  .add(1, "day")
  .startOf("day")
  .toDate();
const fridayOfNextWeek = dayjs(fridayOfCurrentWeek).add(7, "day").toDate();

export const DiscoveryV2DistributionLandingPage = () => {
  const { onTrackSupplierClick, onTrackLandingPageViewed } =
    useDiscoveryV2DistributionTracking();
  const featureFlags = useRemoteFeatureFlags();

  const sdConsolidatedCheckoutEnabled = featureFlags.isActive(
    "sdConsolidatedCheckout",
  );
  const canShowDeliveredBySellar = useCanShowDeliveredBySellarOptionV2();

  const {
    setSearchValue,
    setIsDeliveredBySellar,
    setDeliveredBySellarOption,
    setSelectedSuppliers,
    setOrderCutOffAt,
    reset,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setSearchValue: state.setSearchValue,
      setSelectedSuppliers: state.setSelectedSuppliers,
      setOrderCutOffAt: state.setOrderCutOffAt,
      reset: state.reset,
    })),
  );

  const [openPostcodeDialog, setOpenPostcodeDialog] = useState(false);
  const [previousShouldPromptForPostcode, setPreviousShouldPromptForPostcode] =
    useState(false);
  const { setDisplayTypeOverride } = useMenuDrawerContext();
  const [
    openDistributionInformationDialog,
    setOpenDistributionInformationDialog,
  ] = useState(false);

  const [openDistributionEducationDialog, setOpenDistributionEducationDialog] =
    useState(false);

  const [draftBasketDrawerActive, setDraftBasketDrawerActive] = useState(false);
  const { baskets, totalItemCount } = useDraftDbsBaskets();
  const { trackBasketOpened } = useConsolidatedBasketTracking();
  const openBasket = (source: ConsolidatedBasketOpenSource) => {
    trackBasketOpened(baskets, source);
    setDraftBasketDrawerActive(true);
  };

  const [unAuthorisedUserPostcode, setUnAuthorisedUserPostcode] =
    useLocalStorage<string | undefined>(
      unAuthorisedUserPostcodeInLocalStorageKey,
      undefined,
    );
  const navigate = useNavigate();

  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();

  const buyerPostcode =
    business?.DeliveryAddress.postalCode || unAuthorisedUserPostcode || "";

  const discoveryDistributionSupplierListQuery =
    useDiscoveryDistributionSupplierList(
      {
        query: [
          {
            type: "retailerPostalCode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
      },
      {
        enabled: Boolean(buyerPostcode),
      },
    );

  const suppliers =
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? [];

  const hasSuppliers = suppliers.length > 0;
  const isDeliveryUnavailable =
    !hasSuppliers &&
    !!buyerPostcode &&
    !discoveryDistributionSupplierListQuery.isPending;

  const {
    upcomingDeliveryGroups: thisWeekDeliveryGroups,
    isLoading: isLoadingThisWeekDeliveryGroups,
  } = useGetUpcomingDeliveryGroups({
    buyerPostcode,
    arrivingByDate: {
      low: now,
      high: fridayOfCurrentWeek,
    },
    enabled: Boolean(buyerPostcode) && !isDeliveryUnavailable,
  });

  const { upcomingDeliveryGroups: nextWeekDeliveryGroups } =
    useGetUpcomingDeliveryGroups({
      buyerPostcode,
      enabled:
        Boolean(buyerPostcode) &&
        !isDeliveryUnavailable &&
        thisWeekDeliveryGroups.length <= 0,
      arrivingByDate: {
        low: startOfNextWeek,
        high: fridayOfNextWeek,
      },
    });

  const isAuthenticated = !!user;

  const showUnauthUserPostcodeInNav =
    !isAuthenticated && !!unAuthorisedUserPostcode;

  // If unauthenticated - hide sidebar
  useEffect(() => {
    if (!user) {
      setDisplayTypeOverride("hidden");
    }
    return () => {
      setDisplayTypeOverride(undefined);
    };
  }, [setDisplayTypeOverride, user]);

  const postcodeParams = useSearchParamsPostcodeCheck();
  useEffect(() => {
    if (
      !unAuthorisedUserPostcode &&
      postcodeParams.postcode &&
      postcodeParams.isValid
    ) {
      setUnAuthorisedUserPostcode(postcodeParams.postcode);
    }
  }, [unAuthorisedUserPostcode, postcodeParams, setUnAuthorisedUserPostcode]);

  const shouldPromptForPostcode =
    !unAuthorisedUserPostcode && !postcodeParams.postcode && !isAuthenticated;
  if (shouldPromptForPostcode !== previousShouldPromptForPostcode) {
    setPreviousShouldPromptForPostcode(shouldPromptForPostcode);
    if (shouldPromptForPostcode) {
      setOpenPostcodeDialog(true);
    }
  }

  const trackLandingPageViewed = useEffectEvent(onTrackLandingPageViewed);

  // Track page views on mount
  useEffect(() => {
    trackLandingPageViewed();
  }, []);

  const handleClickSupplier = ({
    supplierId,
    supplierName,
  }: {
    supplierName: string;
    supplierId: number;
  }) => {
    onTrackSupplierClick({
      supplierName,
      source: "delivered-by-sellar-page",
    });
    reset();
    setSearchValue("");
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption("all");
    setSelectedSuppliers([
      {
        id: supplierId,
        name: supplierName,
      },
    ]);

    navigate(DISCOVER_SEARCH);
  };

  const handleClickCard = ({
    filterOption,
    group,
  }: {
    filterOption: DeliveredBySellarFilterOption;
    group: UpcomingDeliveryGroup;
  }) => {
    reset();
    setSearchValue("");
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption(filterOption);
    setSelectedSuppliers([]);
    setOrderCutOffAt(`${group.orderCutOffDateTime}`);
    navigate(DISCOVER_SEARCH);
  };

  return (
    <MarketBasketFeedbackProvider onViewBasket={openBasket}>
      <NavigationBar
        leftIcon={showUnauthUserPostcodeInNav ? "MAP_MARKER" : "CHEVRON_LEFT"}
        onClickLeftIcon={() => {
          if (showUnauthUserPostcodeInNav) {
            setOpenPostcodeDialog(true);
          } else {
            navigate(DISCOVER);
          }
        }}
        title="Delivered by Sellar"
        leftIconText={
          showUnauthUserPostcodeInNav ? unAuthorisedUserPostcode : undefined
        }
        rightComponent={
          sdConsolidatedCheckoutEnabled &&
          canShowDeliveredBySellar &&
          isAuthenticated ? (
            <MarketBasketFlyover onViewBasket={openBasket}>
              <div
                className="flex cursor-pointer items-center gap-1"
                onClick={() => openBasket("navbar")}
              >
                <Icon icon="CART_OUTLINE" size={1} />
                {totalItemCount > 0 && (
                  <QuantityIndicator
                    quantity={totalItemCount}
                    className="bg-[#A34FD2]"
                  />
                )}
              </div>
            </MarketBasketFlyover>
          ) : undefined
        }
      />
      <div className="flex flex-col">
        <DiscoveryV2DistributionLandingPageHeaderV2
          buyerPostcode={buyerPostcode}
          onOpenDistributionEducationDialog={() => {
            setOpenDistributionEducationDialog(true);
          }}
        />

        {isDeliveryUnavailable && (
          <div className="py-6 md:py-8">
            <DiscoveryV2DistributionUnavailablePanel />
          </div>
        )}

        {!isDeliveryUnavailable && isLoadingThisWeekDeliveryGroups && (
          <div className="flex h-32 items-center justify-center">
            <Spinner />
          </div>
        )}

        {!isDeliveryUnavailable && !isLoadingThisWeekDeliveryGroups && (
          <DiscoveryV2DistributionMaxWidthContainer className="mt-4 flex flex-col gap-2 px-4 md:px-8">
            <div className="flex flex-col gap-4">
              {thisWeekDeliveryGroups.length > 0 && (
                <>
                  <div className="flex items-center gap-4 rounded-2xl bg-[#E8D3F4A8] p-4">
                    <div className="flex min-h-11 min-w-11 items-center justify-center rounded-full bg-[#A34FD2] text-white">
                      <Icon icon="CLOCK_OUTLINE" size={0.8} />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <p className="font-semibold text-zinc-800">
                        Get it by the weekend
                      </p>
                      <p className="text-sm text-zinc-600">
                        Order before the stated delivery cut-offs to get
                        products from the following makers delivered together by
                        the weekend
                      </p>
                    </div>
                    <div
                      role="button"
                      onClick={() => {
                        reset();
                        setSearchValue("");
                        setIsDeliveredBySellar(true);
                        setDeliveredBySellarOption("weekend");
                        setSelectedSuppliers([]);
                        setOrderCutOffAt("");
                        navigate(DISCOVER_SEARCH);
                      }}
                      className="flex cursor-pointer items-center gap-1 font-semibold"
                    >
                      Shop all
                      <Icon icon="CHEVRON_RIGHT" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    {thisWeekDeliveryGroups.map((group) => {
                      const canDisplayCountdown = dayjs(
                        group.orderCutOffDateTime,
                      ).isBetween(dayjs().startOf("day"), dayjs().endOf("day"));
                      return (
                        <UpcomingDeliveryGroupCard
                          key={group.key}
                          group={group}
                          displayCountdown={canDisplayCountdown}
                          onClickSupplier={handleClickSupplier}
                          onClickCard={() =>
                            handleClickCard({
                              filterOption: "weekend",
                              group,
                            })
                          }
                        />
                      );
                    })}
                  </div>
                </>
              )}
              {nextWeekDeliveryGroups.length > 0 &&
                thisWeekDeliveryGroups.length <= 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold">
                        Available for next week
                      </span>
                      <Tooltip placement="bottom">
                        <TooltipTrigger className="relative top-1 w-fit">
                          <Icon icon="INFORMATION_OUTLINE" />
                        </TooltipTrigger>
                        <TooltipContent
                          className={
                            "Tooltip w-80 rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-lg"
                          }
                        >
                          <div className="absolute -top-1.75 left-1/2 -translate-x-1/2">
                            <div className="h-3 w-3 rotate-45 transform border-t border-l border-gray-300 bg-white"></div>
                          </div>
                          <div className="flex flex-row items-center gap-2">
                            <Icon
                              icon="INFORMATION_OUTLINE"
                              className="flex-none"
                            />
                            <div className="flex flex-col">
                              <H5>
                                Order earlier in the week for weekend delivery
                              </H5>
                              <H6 className="text-sm">
                                You've missed the cut-offs for delivery by the
                                weekend for some suppliers this week, but you
                                can still order from the full range of suppliers
                                for delivery next week
                              </H6>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex flex-col gap-4">
                      {nextWeekDeliveryGroups.map((group) => (
                        <UpcomingDeliveryGroupCard
                          key={group.key}
                          group={group}
                          displayCountdown={false}
                          onClickSupplier={handleClickSupplier}
                          onClickCard={() =>
                            handleClickCard({
                              filterOption: "next-week",
                              group,
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}
              <div className="flex flex-col gap-4 pb-4">
                <p className="text-lg font-semibold text-zinc-800">
                  All makers delivered by Sellar
                </p>
                <AllMakersSectionList
                  discoveryDistributionSupplierListQuery={
                    discoveryDistributionSupplierListQuery
                  }
                  onClickSupplier={handleClickSupplier}
                />
              </div>
            </div>
          </DiscoveryV2DistributionMaxWidthContainer>
        )}
      </div>
      <DiscoveryV2DistributionInformationDialog
        open={openDistributionInformationDialog}
        onDismiss={() => {
          setOpenDistributionInformationDialog(false);
        }}
        onConfirm={() => {
          setOpenDistributionInformationDialog(false);
        }}
      />
      <StorefrontPostcodeDialog
        active={openPostcodeDialog}
        onDismiss={() => setOpenPostcodeDialog(false)}
        supplier={undefined}
        setUnAuthorisedUserPostcode={setUnAuthorisedUserPostcode}
        title={"Trade ordering from the UK's best craft drinks brands"}
        description={"Enter your venue's postcode to see what's available"}
      />
      <DistributionEducationDialog
        active={openDistributionEducationDialog}
        onDismiss={() => {
          setOpenDistributionEducationDialog(false);
        }}
        initialPage={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}
        buyerPostcode={buyerPostcode}
        supplierId={null}
      />
      {sdConsolidatedCheckoutEnabled && canShowDeliveredBySellar && (
        <DraftBasketDrawer
          active={draftBasketDrawerActive}
          setActive={setDraftBasketDrawerActive}
        />
      )}
    </MarketBasketFeedbackProvider>
  );
};

type DiscoveryV2DistributionMaxWidthContainerProps = {
  children: ReactNode;
  className?: string;
};

const DiscoveryV2DistributionMaxWidthContainer = ({
  children,
  className,
}: DiscoveryV2DistributionMaxWidthContainerProps) => {
  return (
    <div className={twMerge("flex w-full flex-col items-center", className)}>
      <div className="flex w-full max-w-300 flex-col">{children}</div>
    </div>
  );
};

const DiscoveryV2DistributionLandingPageHeaderV2 = ({
  buyerPostcode,
  onOpenDistributionEducationDialog,
}: {
  buyerPostcode: string;
  onOpenDistributionEducationDialog: () => void;
}) => {
  const isMobile = useMediaQuery("(max-width: 767px)");

  const formattedDeliveryDays = useGetFormattedDeliveryDays({
    buyerPostcode,
  });

  const deliveryDays = [
    formattedDeliveryDays ? ": " : "",
    formattedDeliveryDays,
  ]
    .filter(Boolean)
    .join("");

  const deliveryMessage = buyerPostcode ? (
    <p>
      Delivering to <strong>{buyerPostcode}</strong>
      {deliveryDays}
    </p>
  ) : (
    "Delivering"
  );

  if (isMobile) {
    return (
      <div className="flex items-center justify-between p-4">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/app-assets/discovery-distribution-hero-text.png"
          alt="Delivered by Sellar"
          className="max-w-9/12 object-contain"
        />
        <Button className="h-10 w-10 shadow-sm" color="slim">
          <Icon icon="INFORMATION_OUTLINE" />
        </Button>
      </div>
    );
  }

  return (
    <DiscoveryV2DistributionMaxWidthContainer className="bg-white px-4 md:px-8">
      <div className="flex flex-col gap-8 bg-white py-4 md:flex-row md:py-8">
        <div className="order-2 flex w-full flex-col gap-4 md:order-1 md:w-1/2">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/app-assets/discovery-distribution-hero-text.png"
            alt="Delivered by Sellar"
            className="max-w-97.5 object-contain"
          />
          <p className="text-zinc-600">
            Orders delivered together by our expert team on our own vans. No
            MOQs, no delivery charges, no couriers.
          </p>
          <Button
            color="slim"
            onClick={onOpenDistributionEducationDialog}
            className="shadow-sm"
          >
            How does it work?
          </Button>
        </div>
        <div className="relative order-1 w-full md:order-2 md:w-1/2">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/app-assets/distribution-education-landing-page.png"
            alt="Delivery"
            className="h-65 w-full rounded-lg object-cover"
          />
          <div className="absolute bottom-0 flex w-full items-center justify-center gap-2 rounded-b-lg border border-[#D1A7E9] bg-[#E8D3F4] p-3 text-[#8C23C7]">
            <Icon icon="CLOCK_OUTLINE" /> {deliveryMessage}
          </div>
        </div>
      </div>
    </DiscoveryV2DistributionMaxWidthContainer>
  );
};

const DiscoveryV2DistributionUnavailablePanel = () => {
  const isMobile = useMediaQuery("(max-width: 900px)");

  return (
    <DiscoveryV2DistributionMaxWidthContainer className="px-4 md:px-8">
      <div className="flex flex-col overflow-hidden rounded-xl border border-gray-300 bg-gray-50 md:flex-row">
        {!isMobile && (
          <div className="flex flex-none items-center justify-center border-r border-gray-300 bg-zinc-100 md:w-72">
            <img
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/delivered-by-sellar-unavailable.png"
              alt="Delivered by Sellar unavailable"
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <div className="flex flex-col gap-4 p-6 md:p-8">
          <h2 className="text-lg font-semibold">
            We're sorry, Delivered by Sellar isn't available in your area yet
          </h2>
          <p>
            We're expanding our coverage of the U.K. and hope to be delivering
            to your postcode very soon. Register for updates using the button
            below so we can let you know when we're headed your way.
          </p>
          <div>
            <Button
              color="purple"
              onClick={() => {
                window.open(
                  "https://sellar.typeform.com/to/pFgB6wMh",
                  "_blank",
                );
              }}
            >
              Keep me updated <Icon icon="ARROW_TOP_RIGHT" size={0.8} />
            </Button>
          </div>
        </div>
      </div>
    </DiscoveryV2DistributionMaxWidthContainer>
  );
};

const useGetFormattedDeliveryDays = ({
  buyerPostcode,
}: {
  buyerPostcode: string;
}) => {
  const sellarDistributionAvailabilityListQuery =
    useSellarDistributionAvailabilityList(
      {
        query: [
          {
            type: "retailerPostcode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
      },
      {
        enabled: !!buyerPostcode,
      },
    );

  const availabilityList =
    sellarDistributionAvailabilityListQuery.data?.pages.flat() ?? [];

  if (availabilityList.length === 0) {
    return "";
  }

  const deliveryDays = availabilityList.flatMap((availability) => {
    return availability.DistributionScheduleDeliveryDays.map(({ day }) => day);
  });

  const uniqueDeliveryDays = [...new Set(deliveryDays)];

  const deliveryDaysAsIsoWeekdays = uniqueDeliveryDays.map(
    convertDayToIsoWeekday,
  );

  const sortedIsoWeekdayDeliveryDays = deliveryDaysAsIsoWeekdays.toSorted(
    (a, b) => a - b,
  );

  const sortedDeliveryDays = sortedIsoWeekdayDeliveryDays.map((isoWeekday) =>
    dayjs().isoWeekday(isoWeekday).format("ddd"),
  );

  const firstPart = sortedDeliveryDays.slice(0, -1).join(", ");
  const lastPart = sortedDeliveryDays.at(sortedDeliveryDays.length - 1);

  return [firstPart, lastPart].filter(Boolean).join(" & ");
};

const convertDayToIsoWeekday = (
  day: SellarDistributionAvailabilityListResponseType[number]["DistributionScheduleDeliveryDays"][number]["day"],
) => {
  switch (day) {
    case "monday":
      return 1;
    case "tuesday":
      return 2;
    case "wednesday":
      return 3;
    case "thursday":
      return 4;
    case "friday":
      return 5;
    case "saturday":
      return 6;
    case "sunday":
      return 7;
    default:
      return 0;
  }
};

const UpcomingDeliveryGroupCard = ({
  group,
  displayCountdown,
  onClickSupplier,
  onClickCard,
}: {
  group: UpcomingDeliveryGroup;
  displayCountdown: boolean;
  onClickSupplier: (params: {
    supplierName: string;
    supplierId: number;
  }) => void;
  onClickCard: ComponentProps<"div">["onClick"];
}) => {
  return (
    <div
      role="button"
      onClick={onClickCard}
      className="flex cursor-pointer flex-col rounded-lg border border-zinc-200 bg-white shadow-md"
    >
      <UpcomingDeliveryGroupCardHeader
        group={group}
        displayCountdown={displayCountdown}
      />
      <div className="flex flex-col p-4">
        <DiscoveryHorizontalList hideViewMore={true} containerClassName="p-0">
          <div className="flex gap-x-3">
            {group.suppliers.map((supplier) => (
              <DiscoveryV2DistributionSupplierCard
                key={supplier.id}
                onClick={(event) => {
                  event.stopPropagation();
                  onClickSupplier({
                    supplierName: supplier.Business.name,
                    supplierId: supplier.id,
                  });
                }}
                imageUrl={supplier.imageUrl ?? ""}
                logoUrl={supplier.Business.logoUrl ?? ""}
                deliveryAddressCity={supplier.Business.DeliveryAddress.city}
                businessName={supplier.Business.name}
              />
            ))}
          </div>
        </DiscoveryHorizontalList>
      </div>
    </div>
  );
};

const UpcomingDeliveryGroupCardHeader = ({
  group,
  displayCountdown = false,
}: {
  group: UpcomingDeliveryGroup;
  displayCountdown?: boolean;
}) => {
  const isMobile = useMediaQuery("(max-width: 767px)");

  const shopMakerText =
    group.suppliers.length === 1 ? `Shop maker` : "Shop all makers";

  const latestDeliveryDate = group.deliveryDates.reduce((latest, date) =>
    dayjs(date).isAfter(latest) ? date : latest,
  );

  const renderOrderCutOffText = () => (
    <>
      <span>Order before</span>{" "}
      <span className="text-[#8C23C7]">
        {dayjs(group.orderCutOffDateTime).format("h:mma dddd")}
      </span>
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex flex-col items-start">
            <p className="text-start font-semibold">
              {renderOrderCutOffText()}
            </p>
            <div className="inline-flex gap-1 text-zinc-500">
              <p>
                Latest delivery by{" "}
                <span className="font-semibold">
                  {dayjs(latestDeliveryDate).format("ddd DD MMM")}
                </span>
              </p>
              <span>•</span>
              <p className="inline-flex items-center gap-1 underline">
                {group.suppliers.length}{" "}
                {pluralize("maker", group.suppliers.length)}→
              </p>
            </div>
          </div>
          <Icon icon="CHEVRON_RIGHT" />
        </div>
        {displayCountdown && (
          <div className="flex items-center justify-center gap-2 bg-[#E8D3F4A8] py-1 text-xs text-[#6B21A8]">
            <Icon
              icon="CLOCK_FAST_OUTLINE"
              className="relative top-px"
              size={0.7}
            />
            <p className="font-semibold">Cut-off soon!</p>
            <TrackedCutoffCountdown cutoff={group.orderCutOffDateTime} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center border-b border-zinc-200 p-4">
      <div className="flex flex-1 flex-col">
        <div className="flex gap-2">
          <p className="text-lg font-semibold">{renderOrderCutOffText()}</p>
          {displayCountdown && (
            <div className="flex items-center gap-1 rounded-sm bg-[#E8D3F4A8] px-1 py-0.5 text-xs text-[#6B21A8]">
              <Icon icon="CLOCK_FAST_OUTLINE" size={0.6} />
              <p>
                <span className="font-semibold">Cut-off soon!</span>{" "}
                <TrackedCutoffCountdown cutoff={group.orderCutOffDateTime} />
              </p>
            </div>
          )}
        </div>
        <div className="inline-flex gap-1 text-zinc-500">
          <p>
            Latest delivery by{" "}
            <span className="font-semibold">
              {dayjs(latestDeliveryDate).format("ddd DD MMM")}
            </span>
          </p>
          <span>•</span>
          <p>
            {group.suppliers.length}{" "}
            {pluralize("maker", group.suppliers.length)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 font-semibold">
        {shopMakerText}
        <Icon icon="CHEVRON_RIGHT" />
      </div>
    </div>
  );
};

type AllMakersSectionProps = {
  onClickSupplier: (params: {
    supplierName: string;
    supplierId: number;
  }) => void;
  discoveryDistributionSupplierListQuery: ReturnType<
    typeof useDiscoveryDistributionSupplierList<DiscoveryDistributionSupplierListResponseType>
  >;
};

const AllMakersSectionList = ({
  discoveryDistributionSupplierListQuery,
  onClickSupplier,
}: AllMakersSectionProps) => {
  const suppliers =
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? [];

  const isMobile = useMediaQuery("(max-width: 767px)");

  if (isMobile) {
    return (
      <div className="flex flex-col gap-2">
        {suppliers.map((supplier) => {
          const location = [
            supplier.Business.DeliveryAddress.city,
            supplier.Business.DeliveryAddress.region,
          ]
            .filter(Boolean)
            .join(", ");

          return (
            <div
              role="button"
              onClick={() => {
                onClickSupplier({
                  supplierName: supplier.Business.name,
                  supplierId: supplier.id,
                });
              }}
              key={supplier.id}
              className="flex cursor-pointer items-center justify-between rounded-2xl border border-zinc-300 bg-white p-3 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <ProfilePicture
                  size="large"
                  logoUrl={supplier.Business.logoUrl}
                  name={supplier.Business.name}
                  className="rounded-full"
                  fallback="supplier-badge"
                />
                <div className="flex flex-col text-sm">
                  <p className="font-semibold">{supplier.Business.name}</p>
                  <p className="text-zinc-500">{location}</p>
                </div>
              </div>
              <Icon icon="CHEVRON_RIGHT" />
            </div>
          );
        })}
        <IntersectingInfiniteLoadingButton
          query={discoveryDistributionSupplierListQuery}
        />
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "grid grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3",
      )}
    >
      {suppliers.map((supplier) => (
        <DiscoveryListSupplierCard
          key={supplier.id}
          supplier={{ ...supplier, containerTypes: [] }}
          onClick={() => {
            onClickSupplier({
              supplierName: supplier.Business.name,
              supplierId: supplier.id,
            });
          }}
        />
      ))}
      <IntersectingInfiniteLoadingButton
        query={discoveryDistributionSupplierListQuery}
      />
    </div>
  );
};
